// Vendors
import $ from 'jquery';
import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import axios from 'axios';

// Components
import globalLoader from '../../../site/global-loader';

// Requests
import { getModalRequest } from '../../../requests';

// Utilities
import { bodyScroll } from '../../../lib/utilities';

const LOADING_STATE_NONE = 'NONE';
const LOADING_STATE_LOADING = 'LOADING';
const LOADING_STATE_LOADED = 'LOADED';
const LOADING_STATE_ERROR = 'ERROR';

const DynamicModal = () => {
  const defaultSettings = {
    size: 'md',
    width: null,
    hasPadding: true,
    verticalAlignment: 'center'
  };

  const hashPrefix = '#modal:';
  const [content, setContent] = useState('');
  const [settings, setSettings] = useState(defaultSettings);
  const [hubspotFormId, setHubspotFormId] = useState('');
  const [isOpen, setOpen] = useState(false);
  const [loadingState, setLoadingState] = useState(LOADING_STATE_NONE);
  const hasCustomWidth = settings.size === 'custom' && settings.width;
  const customWidth = hasCustomWidth ? `${settings.width}px` : null;
  const htmlElement = document.documentElement;
  const hubspotFormContainerID = 'modal-hubspot-form';

  const modalClassName = classnames(
    'modal',
    `modal--${settings.size}`,
    `modal--valign-${settings.verticalAlignment}`,
    {
      'modal--visible': isOpen,
      [`modal--${settings.size}`]: !hasCustomWidth
    }
  );

  const modalContentClassName = classnames('modal__content', {
    'u--padding-0': !settings.hasPadding
  });

  const modalDialogStyle = {
    maxWidth: customWidth,
    flexBasis: customWidth
  };

  function appendFormContainerToContent(content, hubspotFormId) {
    if (!hubspotFormId) return content;
    return `${content}<div id="${hubspotFormContainerID}" class="hbspt-form"></div>`;
  }

  function hubspotScriptExists(url) {
    const scriptTags = document.getElementsByTagName('script');

    for (let i = scriptTags.length; --i; ) {
      if (scriptTags[i].src.includes(url)) return true;
    }

    return false;
  }

  function injectHubspotForm(formId, target) {
    const hsScriptURL = '//js.hsforms.net/forms/v2.js';

    if (!hubspotScriptExists(hsScriptURL)) {
      const scriptTag = document.createElement('script');
      scriptTag.src = '//js.hsforms.net/forms/v2.js';
      document.body.appendChild(scriptTag);
      scriptTag.addEventListener('load', () => injectForm());
    } else {
      injectForm();
    }

    function injectForm() {
      if (window.hbspt) {
        window.hbspt.forms.create({
          portalId: '3572053',
          target,
          formId
        });
      }
    }
  }

  const ModalError = () => (
    <div className="notice notice--error">Modal not found.</div>
  );

  useEffect(() => {
    const modalTriggers = document.querySelectorAll(`[href*="${hashPrefix}"]`);
    const { searchParams, hash } = new URL(window.location);
    let slug = null;

    if (hash && hash.indexOf(hashPrefix) > -1)
      slug = hash.split(hashPrefix).pop();

    if (searchParams.get('modal')) slug = searchParams.get('modal');

    if (slug) openModal(slug);

    modalTriggers.forEach((element) =>
      element.addEventListener('click', handleOpenClick)
    );

    return () =>
      modalTriggers.forEach((element) =>
        element.removeEventListener('click', handleOpenClick)
      );
  }, []);

  function openModal(slug) {
    globalLoader.show();
    setLoadingState(LOADING_STATE_LOADING);

    getModalRequest(slug)
      .then((data) => {
        const { content = '', settings = {}, hubspot_form_id } = data;
        const { alternateMobileView = {} } = settings;

        if (
          alternateMobileView.enabled &&
          alternateMobileView.url &&
          alternateMobileView.breakpoint &&
          window.outerWidth < alternateMobileView.breakpoint
        ) {
          setLoadingState(LOADING_STATE_NONE);
          bodyScroll.enable();
          globalLoader.hide();
          window.location = alternateMobileView.url;
          return;
        }

        setSettings({ ...defaultSettings, ...settings });
        setHubspotFormId(hubspot_form_id);
        setContent(appendFormContainerToContent(content, hubspot_form_id));
        setLoadingState(LOADING_STATE_LOADED);
        setOpen(true);
        setTimeout(() => {
          bodyScroll.disable();
          globalLoader.hide();
          htmlElement.classList.add('html--modal-visible');
        }, 300);
      })
      .catch((error) => {
        if (error.response && error.response.status === 404) {
          setLoadingState(LOADING_STATE_ERROR);
          setOpen(true);
          setTimeout(() => {
            bodyScroll.disable();
            globalLoader.hide();
            htmlElement.classList.add('html--modal-visible');
          }, 300);
          return;
        }

        if (error.message && error.message !== 'Modal request cancelled') {
          setLoadingState(LOADING_STATE_NONE);
          bodyScroll.enable();
          globalLoader.hide();
          return;
        }
      });
  }

  function handleOpenClick(event) {
    event.preventDefault();

    const trigger = event.currentTarget;
    const slug = trigger.href.split(hashPrefix).pop();

    openModal(slug);
  }

  function handleCloseClick(event) {
    event.preventDefault();
    setOpen(false);
    setTimeout(() => {
      setContent('');
      bodyScroll.enable();
      htmlElement.classList.remove('html--modal-visible');
      setSettings(defaultSettings);
      setLoadingState(LOADING_STATE_NONE);
    }, 300);
  }

  function handleModalDialogClick(event) {
    event.stopPropagation();
  }

  return (
    <div
      className={modalClassName}
      aria-labelledby="app-modal-label"
      role="dialog"
      onClick={handleCloseClick}
    >
      <div
        className="modal__dialog"
        role="document"
        style={modalDialogStyle}
        onClick={handleModalDialogClick}
      >
        <button
          type="button"
          className="modal__close module-hover-root"
          aria-label="Close"
          onClick={handleCloseClick}
        >
          <span className="modal__close-icon" /> Close
        </button>
        <div className={modalContentClassName}>
          <section className="modal__body">
            {loadingState === LOADING_STATE_LOADED && (
              <div dangerouslySetInnerHTML={{ __html: content }} />
            )}
            {loadingState === LOADING_STATE_ERROR && <ModalError />}
            {hubspotFormId &&
              ((<div id={hubspotFormContainerID} />),
              injectHubspotForm(hubspotFormId, `#${hubspotFormContainerID}`))}
          </section>
        </div>
      </div>
    </div>
  );
};

export default DynamicModal;
